import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BlogDisplay = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});

  // --- STYLES ---
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    minHeight: '100vh'
  };

  // UPDATED: Grid style for 4 columns on large screens
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', 
    gap: '20px',
    marginTop: '20px'
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const response = await Promise.race([
        axios.get('https://mithahomesfrontend.pemixcels.com/api/blogs/all'),
        timeoutPromise
      ]);
      
      if (response.data.blogs) {
        const sortedBlogs = response.data.blogs.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setBlogs(sortedBlogs);
        preloadImages(sortedBlogs);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message === 'Request timeout' 
        ? 'Request took too long. Please try again.' 
        : 'Failed to fetch blogs. Please check your connection.'
      );
      setLoading(false);
    }
  };

  // Preload images for better user experience
  const preloadImages = (blogs) => {
    blogs.forEach(blog => {
      const img = new Image();
      img.src = getFullImageUrl(blog.image);
      img.onload = () => {
        setLoadedImages(prev => ({ ...prev, [blog.id]: true }));
      };
    });
  };

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=contain&w=800';
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const baseUrl = 'https://mithahomesfrontend.pemixcels.com';
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  const BlogCard = ({ blog }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

    const cardStyle = {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      textDecoration: 'none', 
      color: 'inherit',
      position: 'relative'
    };

    const cardHoverStyle = {
      transform: 'translateY(-5px)',
      boxShadow: '0 12px 25px rgba(0,0,0,0.15)'
    };

    const imageContainerStyle = {
      width: '100%',
      minHeight: '180px',
      position: 'relative',
      backgroundColor: '#f1f5f9',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    const imageStyle = {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain', // Changed from 'cover' to 'contain' to show full image
      display: 'block',
      opacity: imgLoaded ? 1 : 0,
      transition: 'opacity 0.3s ease',
      width: 'auto',
      height: 'auto'
    };

    const loadingStyle = {
      width: '100%',
      height: '100%',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: 0,
      left: 0
    };

    const categoryBadgeStyle = {
      position: 'absolute',
      top: '10px',
      left: '10px',
      backgroundColor: 'rgba(37,99,235,0.9)',
      color: '#fff',
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '600',
      zIndex: 2
    };

    const contentStyle = {
      padding: '15px',
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    };

    const titleTextStyle = {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 8px 0',
      lineHeight: '1.4',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      justifyContent: 'center'
    };

    const dateStyle = {
      fontSize: '12px',
      color: '#94a3b8',
      marginTop: 'auto',
      paddingTop: '8px'
    };

    const imageUrl = useMemo(() => getFullImageUrl(blog.image), [blog.image]);

    const handleImageLoad = (e) => {
      setImgLoaded(true);
      setImageDimensions({
        width: e.target.naturalWidth,
        height: e.target.naturalHeight
      });
    };

    return (
      <Link
        to={`/blog/${blog.id}`}
        style={{ ...cardStyle, ...(isHovered && cardHoverStyle) }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={imageContainerStyle}>
          
          {/* Loading skeleton */}
          {!imgLoaded && !imgError && (
            <div style={loadingStyle}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '3px solid #e2e8f0',
                borderTop: '3px solid #3b82f6',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
          )}
          
          {/* Error fallback */}
          {imgError && (
            <div style={loadingStyle}>
              <span style={{ color: '#94a3b8', fontSize: '14px' }}>Image not available</span>
            </div>
          )}
          
          {/* Actual image - FULL IMAGE DISPLAY */}
          <img
            src={imageUrl}
            alt={blog.title}
            style={{
              ...imageStyle,
              // Adjust based on image orientation
              ...(imageDimensions.width > imageDimensions.height ? {
                width: '100%',
                height: 'auto'
              } : {
                width: 'auto',
                height: '100%'
              })
            }}
            loading="lazy"
            onLoad={handleImageLoad}
            onError={(e) => {
              setImgError(true);
              setImgLoaded(true);
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <div style={contentStyle}>
          <center>
             <h3 style={titleTextStyle} title={blog.title}>
            {blog.title}
          </h3>
          </center>
         
         
        </div>
      </Link>
    );
  };

  const GlobalStyles = () => (
    <style>
      {`
        body { 
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          margin: 0; 
          min-height: 100vh;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (min-width: 1024px) {
          .blog-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        
        @media (max-width: 1023px) and (min-width: 768px) {
          .blog-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        
        @media (max-width: 767px) {
          .blog-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 480px) {
          .blog-grid {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* Ensure images display fully */
        img {
          transition: opacity 0.3s ease;
          object-fit: contain !important;
        }
        
        /* Card image container */
        .blog-card-image-container {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }
      `}
    </style>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div style={gridStyle} className="blog-grid">
      {[...Array(8)].map((_, index) => (
        <div key={index} style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          height: '350px',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          <div style={{
            width: '100%',
            height: '200px',
            backgroundColor: '#e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '60%',
              height: '60%',
              backgroundColor: '#cbd5e1',
              borderRadius: '4px'
            }}></div>
          </div>
          <div style={{ padding: '15px' }}>
            <div style={{
              height: '20px',
              backgroundColor: '#e2e8f0',
              borderRadius: '4px',
              marginBottom: '10px'
            }}></div>
            <div style={{
              height: '16px',
              width: '60%',
              backgroundColor: '#e2e8f0',
              borderRadius: '4px'
            }}></div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={containerStyle}>
      <GlobalStyles />
      
    

      {/* Content */}
      {loading ? (
        <LoadingSkeleton />
      ) : error ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📰</div>
          <h3 style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</h3>
          <button 
            onClick={fetchBlogs}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              marginTop: '20px'
            }}
          >
            Try Again
          </button>
        </div>
      ) : blogs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📝</div>
          <h3 style={{ color: '#64748b' }}>No blog posts available</h3>
        </div>
      ) : (
        <>
          <div style={gridStyle} className="blog-grid">
            {blogs.map((blog) => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>
       
        </>
      )}
    </div>
  );
};

export default BlogDisplay;